import React from 'react';
import { Lock, Server, Key, ShieldCheck, Eye, RefreshCw, AlertTriangle, Mail } from 'lucide-react';
import styles from '../../styles/institutional.module.css';

const SECURITY_CARDS = [
    {
        icon: <Lock size={20} />,
        title: 'HTTPS em tudo',
        desc: 'Toda comunicação entre seu navegador e nossos servidores usa TLS 1.3. Seus dados nunca trafegam em texto simples.',
    },
    {
        icon: <Key size={20} />,
        title: 'Senhas com bcrypt',
        desc: 'Senhas são transformadas em hash bcrypt com salt 12 antes de serem salvas. Nem nossa equipe consegue ler sua senha.',
    },
    {
        icon: <Server size={20} />,
        title: 'Banco de dados criptografado',
        desc: 'Dados armazenados no MongoDB Atlas com criptografia em repouso (AES-256). Backups automáticos diários.',
    },
    {
        icon: <ShieldCheck size={20} />,
        title: 'Autenticação JWT',
        desc: 'Tokens de sessão assinados com chave secreta e validade definida. Sessões inválidas são rejeitadas automaticamente.',
    },
    {
        icon: <Eye size={20} />,
        title: 'Isolamento de dados',
        desc: 'Cada usuário acessa apenas seus próprios dados. Verificações de propriedade em todas as rotas da API.',
    },
    {
        icon: <RefreshCw size={20} />,
        title: 'Backup automático',
        desc: 'Backup diário dos seus documentos. Em caso de falha, seus dados podem ser restaurados rapidamente.',
    },
];

const SECTIONS = [
    {
        title: 'Nossa infraestrutura',
        content: 'O Orçamento Digital é hospedado no Render.com (frontend e backend) com banco de dados no MongoDB Atlas. Ambas as plataformas são líderes em segurança para aplicações SaaS e seguem padrões internacionais como SOC 2 Type II.',
        list: null,
    },
    {
        title: 'O que você pode fazer para se proteger',
        content: null,
        list: [
            'Use uma senha forte e única para sua conta Orçamento Digital',
            'Nunca compartilhe suas credenciais com terceiros',
            'Faça logout em computadores compartilhados',
            'Verifique sempre se está no domínio correto (orcamento-digital.com.br)',
            'Desconfie de e-mails pedindo sua senha — nunca solicitamos isso',
        ],
    },
    {
        title: 'Pagamentos seguros',
        content: 'Não armazenamos nenhum dado de cartão de crédito ou débito. Todos os pagamentos são processados diretamente pelo Mercado Pago, que possui certificação PCI DSS nível 1 — o mais alto padrão de segurança para transações financeiras.',
        list: null,
    },
    {
        title: 'Conformidade',
        content: null,
        list: [
            'LGPD (Lei Geral de Proteção de Dados — Lei nº 13.709/2018)',
            'OWASP Top 10 — proteção contra as principais vulnerabilidades web',
            'Boas práticas de segurança OWASP para APIs REST',
            'Tokens JWT com expiração e rotação de segredo',
        ],
    },
    {
        title: 'Divulgação responsável de vulnerabilidades',
        content: 'Encontrou uma vulnerabilidade de segurança? Agradecemos a divulgação responsável. Entre em contato antes de publicar qualquer informação para que possamos corrigir o problema.',
        list: null,
    },
];

export default function Seguranca() {
    return (
        <div className={styles.page}>
            <div className={styles.proseWrap}>

                {/* Header */}
                <div className={styles.proseHero}>
                    <div className={styles.proseBadge}>
                        <ShieldCheck size={11} style={{ display:'inline', marginRight:'4px', verticalAlign:'middle' }} />
                        Segurança
                    </div>
                    <h1 className={styles.proseTitle}>Sua segurança é nossa prioridade</h1>
                    <p className={styles.proseDate}>Práticas e tecnologias que protegem seus dados 24/7</p>
                </div>

                {/* Cards de segurança */}
                <div className={styles.securityGrid}>
                    {SECURITY_CARDS.map((card, i) => (
                        <div key={i} className={styles.securityCard}>
                            <div className={styles.securityIcon}>{card.icon}</div>
                            <h3 className={styles.securityCardTitle}>{card.title}</h3>
                            <p className={styles.securityCardDesc}>{card.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Seções de texto */}
                <div className={styles.proseBody}>
                    <p className={styles.proseHighlight}>
                        Levamos segurança a sério em cada camada do sistema — do banco de dados até o navegador.
                        Abaixo explicamos as práticas que adotamos para proteger você.
                    </p>

                    {SECTIONS.map((section, i) => (
                        <div key={i} className={styles.proseSection}>
                            <h2 className={styles.proseSectionTitle}>{section.title}</h2>
                            {section.content && (
                                <p className={styles.proseText}>{section.content}</p>
                            )}
                            {section.list && (
                                <ul className={styles.proseList}>
                                    {section.list.map((item, j) => (
                                        <li key={j}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}

                    {/* Contato de segurança */}
                    <div className={styles.proseSection}>
                        <h2 className={styles.proseSectionTitle}>Reportar uma vulnerabilidade</h2>
                        <div className={styles.contactBox}>
                            <AlertTriangle size={18} className={styles.contactIcon} />
                            <div>
                                <p className={styles.proseText} style={{ margin: 0 }}>
                                    Encontrou algo suspeito? Entre em contato:
                                </p>
                                <a href="mailto:seguranca@notafacil.com.br" className={styles.contactEmail}>
                                    <Mail size={14} />
                                    seguranca@notafacil.com.br
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}